import produce from 'immer'

import {
  DespatchAdvice,
  Invoice,
  OrderResponse,
  WebEdiDto,
} from '../../../../data/models'

interface ResponsePeriod {
  Start: Date
  End: Date
}

interface Payload
  extends Omit<
    OrderResponse | DespatchAdvice | Invoice,
    | 'ConfirmationDate'
    | 'DeliveryDate'
    | 'DeliveryNoteDate'
    | 'InvoiceDate'
    | 'OrderDate'
    | 'OrderDeliveryDate'
    | 'ResponsePeriod'
  > {
  ConfirmationDate: Date
  DeliveryDate: Date
  DeliveryNoteDate: Date
  InvoiceDate: Date
  OrderDate: Date
  OrderDeliveryDate: Date
  ResponsePeriod: ResponsePeriod
}
interface ConvertedResponse
  extends Omit<
    WebEdiDto<OrderResponse | DespatchAdvice | Invoice>,
    'CreationDate' | 'Payload'
  > {
  CreationDate: Date
  Payload: Payload
}

/**
 * Converts a list of WebEdiDto responses into a list of converted order objects.
 *
 * This function uses the Immer library to create a new array of converted order objects from
 * the provided WebEdiDto array. The conversion process involves:
 *
 * 1. Transforming date strings into Date objects for easier filtering and sorting in tables.
 *
 * @param {WebEdiDto<OrderResponse | DespatchAdvice | Invoice>[] | undefined} responses - The list of responses to be converted.
 * @returns {ConvertedResponse[]} - The list of converted responses.
 */
export function convertOrder(
  responses: WebEdiDto<OrderResponse | DespatchAdvice | Invoice>[] | undefined,
) {
  // transforms date strings in date objects so it can be filtered in tables
  const convertedResponses = produce(
    responses,
    (draft: ConvertedResponse[]) => {
      draft?.forEach(response => {
        response.CreationDate = new Date(response.CreationDate)
        response.Payload.ConfirmationDate = new Date(
          response.Payload.ConfirmationDate,
        )
        response.Payload.DeliveryDate = new Date(response.Payload.DeliveryDate)
        response.Payload.DeliveryNoteDate = new Date(
          response.Payload.DeliveryNoteDate,
        )
        response.Payload.InvoiceDate = new Date(response.Payload.InvoiceDate)
        response.Payload.OrderDate = new Date(response.Payload.OrderDate)
        response.Payload.OrderDeliveryDate = new Date(
          response.Payload.OrderDeliveryDate,
        )

        if (response.Payload.ResponsePeriod) {
          response.Payload.ResponsePeriod.Start = new Date(
            response.Payload.ResponsePeriod.Start,
          )
          response.Payload.ResponsePeriod.End = new Date(
            response.Payload.ResponsePeriod.End,
          )
        }
      })
    },
  )

  return convertedResponses
}
